import type { PractitionerDto } from "@repo/data-store";
import { CommandEmpty, CommandGroup, CommandItem } from "@repo/ui/components/ui/command";

export interface PractitionerSearchListProps {
  practitioners: PractitionerDto[];
  /** Answers with the FHIR locator, which is what every caller actually wants. */
  onSelect: (practitionerLocator: string) => void;
  /** Optional render to prepend inside each item (e.g. check icon) */
  renderPrefix?: (practitioner: PractitionerDto) => React.ReactNode;
}

function formatName(practitioner: PractitionerDto): string {
  const given = practitioner.name.given?.join(" ") ?? "";
  const family = practitioner.name.family ?? "";
  return `${given} ${family}`.trim() || "Unknown";
}

export function PractitionerSearchList({ practitioners, onSelect, renderPrefix }: PractitionerSearchListProps) {
  return (
    <>
      <CommandEmpty>No colleagues found.</CommandEmpty>
      <CommandGroup>
        {practitioners
          // A row without a locator cannot be addressed — there is nothing to
          // hand a caller — so it is left out rather than offered and refused.
          .filter((practitioner) => practitioner.fhirLocator)
          .map((practitioner) => {
            const name = formatName(practitioner);
            const email = practitioner.telecom?.find((t) => t.system === "email")?.value;
            const details = [practitioner.role, email].filter(Boolean).join(" · ");

            return (
              <CommandItem
                key={practitioner.practitionerId}
                value={name}
                onSelect={() => onSelect(practitioner.fhirLocator as string)}
              >
                {renderPrefix?.(practitioner)}
                <div className="flex flex-col">
                  <span>{name}</span>
                  {details && <span className="text-xs text-muted-foreground">{details}</span>}
                </div>
              </CommandItem>
            );
          })}
      </CommandGroup>
    </>
  );
}
